import { ChangeEvent, CSSProperties, RefObject, useEffect, useRef } from "react";
import { Asterisk, Colors, ErrorAlert } from "main";

// Note: Adds placeholder text styling
import "./TextAreaInput.css";

/********* Styles ********/
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  } as CSSProperties,
  label: {
    fontWeight: 600,
    color: Colors.black,
  } as CSSProperties,
  subLabel: {
    lineHeight: "24px",
    fontSize: 14,
    margin: 0,
  } as CSSProperties,
  inputWrapper: ({
    isDisabled,
    errorMessage,
  }: Pick<TextAreaInputProps, "isDisabled" | "errorMessage">) =>
    ({
      borderBottom: `1px solid ${errorMessage ? Colors.red500 : isDisabled ? "transparent" : Colors.slate400}`,
    }) as CSSProperties,
  nativeInput: ({ isDisabled }: Pick<TextAreaInputProps, "isDisabled">) =>
    ({
      padding: 0,
      border: 0,
      width: "100%",
      maxWidth: "100%",
      maxHeight: "100%",
      color: isDisabled ? Colors.slate400 : Colors.black,
      background: "transparent",
      outline: "none",
      appearance: "none",
      touchAction: "manipulation",
      resize: "none",
    }) as CSSProperties,
};

/********* Types ********/
export type TextAreaInputProps = {
  label?: string;
  subLabel?: string;
  value?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  errorMessage?: string;
  onChange: (value: string) => void;
};

export function TextAreaInput(props: TextAreaInputProps) {
  const {
    value,
    label,
    placeholder,
    onChange,
    isDisabled,
    isRequired = false,
    subLabel,
    errorMessage,
  } = props;

  /********* Constants ********/
  // Defaulting to an empty string to remove `controlled` input error.
  const _value = value || "";

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useAutoSizeTextArea(textAreaRef, _value);

  /********* Render ********/
  return (
    <>
      <label style={styles.container} className="container">
        {label && (
          <div style={styles.label} className="label">
            {label}
            {/* TODO match types for asterisk */}
            {isRequired && <Asterisk />}
          </div>
        )}
        {subLabel && <p style={styles.subLabel}>{subLabel}</p>}
        <div style={styles.inputWrapper({ isDisabled, errorMessage })} className="input-wrapper">
          <textarea
            className="placeHolderText"
            style={styles.nativeInput({ isDisabled })}
            disabled={isDisabled}
            autoCapitalize="off"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            aria-label={label}
            placeholder={placeholder}
            value={_value}
            ref={textAreaRef}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
          />
        </div>
      </label>
      {errorMessage && (
        <ErrorAlert>
          <p style={{ margin: 0 }}>{errorMessage}</p>
        </ErrorAlert>
      )}
    </>
  );
}

// Updates the height of a <textarea> when the value changes.
const useAutoSizeTextArea = (textAreaRef: RefObject<HTMLTextAreaElement>, value: string) => {
  useEffect(() => {
    if (!textAreaRef.current) return;

    const textArea = textAreaRef.current;

    const adjustHeight = () => {
      if (!textArea) return;
      // Reset the height to get the correct scrollHeight
      textArea.style.height = "0px";
      textArea.style.height = `${textArea.scrollHeight}px`;
    };

    const observerCallback: ResizeObserverCallback = (entries) => {
      // Using requestAnimationFrame to prevent infinite resize loops
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || entries.length === 0) return;
        // Adjust the height on value change
        adjustHeight();
      });
    };

    const resizeObserver = new ResizeObserver(observerCallback);
    resizeObserver.observe(textArea);

    // Initial height adjustment
    adjustHeight();

    return () => {
      resizeObserver.disconnect();
    };
  }, [textAreaRef, value]);
};
